// src/components/NewsDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Button, Divider, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchNewsDetail } from '../lib/api';

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return `${Math.floor(diff / 60000)}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const NewsDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getNewsDetail = async () => {
      try {
        const data = await fetchNewsDetail(id);
        setArticle(data);
      } catch (error) {
        console.error('記事の取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };
    getNewsDetail();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2, md: 4 }, py: 8 }}>
        <Typography sx={{ color: '#a8a29e', fontFamily: 'inherit', fontSize: '0.82rem' }}>
          読み込み中...
        </Typography>
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2, md: 4 }, py: 8 }}>
        <Typography sx={{ color: '#78716c', fontFamily: 'inherit', mb: 3 }}>
          記事が見つかりませんでした。
        </Typography>
        <Button component={Link} to="/" startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: '0.78rem', fontFamily: 'inherit', color: '#78716c', p: 0, '&:hover': { backgroundColor: 'transparent', color: '#1c1916' } }}>
          Back to feed
        </Button>
      </Box>
    );
  }

  const ago = timeAgo(article.created_at);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
      {/* Back */}
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
        sx={{
          fontSize: '0.75rem',
          fontFamily: 'inherit',
          color: '#78716c',
          p: 0,
          mb: 3,
          '&:hover': { color: '#1c1916', backgroundColor: 'transparent' },
        }}
      >
        Back to feed
      </Button>

      {/* Meta chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {article.SITE_NAME && (
          <Chip
            label={article.SITE_NAME}
            size="small"
            sx={{
              height: 17,
              fontSize: '0.65rem',
              fontFamily: 'inherit',
              fontWeight: 700,
              letterSpacing: '0.04em',
              backgroundColor: '#ddd9d2',
              color: '#78716c',
              border: '1px solid #ccc8c2',
            }}
          />
        )}
        {ago && (
          <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e', fontFamily: 'inherit' }}>
            {ago}
          </Typography>
        )}
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1.3rem', md: '1.6rem' },
          lineHeight: 1.35,
          color: '#1c1916',
          fontFamily: 'inherit',
          mb: 3,
        }}
      >
        {article.TITLE}
      </Typography>

      <Divider sx={{ borderColor: '#ccc8c2', mb: 3 }} />

      {/* Body sections */}
      {[1, 2, 3].map((num) => {
        const headline = article[`HEADLINE${num}`];
        const body = article[`HEADLINE${num}_BODY`];
        return headline && body ? (
          <Box key={num} sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1c1916', fontFamily: 'inherit', mb: 0.75 }}>
              {headline}
            </Typography>
            <Typography sx={{ fontSize: '0.87rem', lineHeight: 1.8, color: '#44403c', fontFamily: 'inherit' }}>
              {body}
            </Typography>
          </Box>
        ) : null;
      })}

      {/* Source */}
      {article.URL && (
        <Box
          sx={{
            mt: 4,
            p: 1.5,
            border: '1px solid #ccc8c2',
            borderRadius: 1,
            backgroundColor: '#e8e4de',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#a8a29e', fontFamily: 'inherit', letterSpacing: '0.06em', mb: 0.3 }}>
              SOURCE
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#78716c', fontFamily: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {article.SITE_NAME} — {article.RAW_TITLE}
            </Typography>
          </Box>
          <Button
            component="a"
            href={article.URL}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
            size="small"
            sx={{
              fontSize: '0.72rem',
              fontFamily: 'inherit',
              fontWeight: 700,
              color: '#1c1916',
              backgroundColor: '#ddd9d2',
              border: '1px solid #ccc8c2',
              px: 1.5,
              borderRadius: 1,
              flexShrink: 0,
              '&:hover': { backgroundColor: '#ccc8c2' },
            }}
          >
            Read
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NewsDetail;
